import { useEffect } from 'react';
import EventEmitter from 'eventemitter3';

type ModalOpenTrackerEvents = {
  change: () => void;
};

/**
 * Singleton counter for tracking how many Kibble Modals/BottomSheets are currently open.
 *
 * Uses an EventEmitter pattern to allow the GlobalPromptStore to subscribe
 * to changes and delay showing prompts while any modals/sheets are open.
 *
 * Uses a counter instead of a boolean because multiple modals/sheets
 * can be open at the same time (even if they shouldn't be).
 */
class ModalOpenTrackerClass extends EventEmitter<ModalOpenTrackerEvents> {
  private openCount = 0;

  // --- useSyncExternalStore interface ---
  subscribe = (listener: () => void): (() => void) => {
    this.on('change', listener);
    return () => {
      this.off('change', listener);
    };
  };

  getSnapshot = (): boolean => {
    return this.openCount > 0;
  };

  // --- Public API methods ---
  increment = (): void => {
    this.openCount += 1;
    this.emit('change');
  };

  decrement = (): void => {
    this.openCount = Math.max(0, this.openCount - 1);
    this.emit('change');
  };

  /** For testing only */
  _reset = (): void => {
    this.openCount = 0;
    this.emit('change');
  };
}

const modalOpenTracker = new ModalOpenTrackerClass();

/**
 * Hook to track the open state of modals/bottom sheets
 * Increments the tracker when `isOpen` becomes true,
 * and decrements when `isOpen` becomes false or when the component unmounts.
 */
export const useModalOpenTrackerEffect = (isOpen: boolean): void => {
  useEffect(() => {
    if (isOpen) {
      modalOpenTracker.increment();
      return () => {
        modalOpenTracker.decrement();
      };
    }
    return undefined;
  }, [isOpen]);
};

export default modalOpenTracker;
